// "Ask Elariom": a chat with Elariom on the web, the same Elariom as in Teams, answering about
// this workspace from its data (rates, live carrier offers, places, margins).
// POST /api/admin/elariom/:org/ask with the conversation so far.

// React imports
import { useEffect, useRef, useState } from 'react';

// Other imports
import axios from 'axios';

// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useTheme, alpha } from '@mui/material/styles';

// Local imports
import { SectionTitle } from './ElariomParts.js';

const EXAMPLES = [
    'Do you have rates from China to Malta?',
    'Shanghai to Haifa, 1x40\' Dry, from next week',
    'What did we pay last for Leixões to Kampong Saom?',
];

const storageKey = orgId => `elariom-ask-${orgId}`;

function Bubble({ role, text, update }) {
    const theme = useTheme();
    const mine = role === 'user';
    return (
        <Box sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
            <Box sx={{
                maxWidth: '78%', padding: '10px 14px', borderRadius: '10px', fontSize: '13px', lineHeight: 1.55, whiteSpace: 'pre-wrap',
                background: mine ? theme.palette.text.primary : '#FFFFFF',
                color: mine ? '#FFFFFF' : theme.palette.text.primary,
                border: mine ? 'none' : `1px solid ${theme.palette.border1}`,
            }}>
                {text}
            </Box>
        </Box>
    );
}

function ElariomAsk({ org, accesstoken }) {
    const theme = useTheme();
    const [ messages, setMessages ] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem(storageKey(org?.id)) || '[]'); } catch (e) { return []; }
    });
    const [ draft, setDraft ] = useState('');
    const [ asking, setAsking ] = useState(false);
    const [ error, setError ] = useState(null);
    const endRef = useRef(null);

    useEffect(() => {
        try { sessionStorage.setItem(storageKey(org?.id), JSON.stringify(messages.slice(-40))); } catch (e) { /* not kept */ }
        endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [messages, asking, org?.id]);

    async function ask(text) {
        const question = (text ?? draft).trim();
        if (!question || asking) return;
        const next = [...messages, { role: 'user', text: question }];
        setMessages(next);
        setDraft('');
        setAsking(true);
        setError(null);
        try {
            const { data } = await axios.post(`/api/admin/elariom/${org.id}/ask?accesstoken=${accesstoken}`, {
                messages: next.map(m => ({ role: m.role, text: m.text })),
            });
            setMessages(m => [
                ...m,
                ...(data.updates || []).map(u => ({ role: 'assistant', text: u, update: true })),
                { role: 'assistant', text: data.reply || '(no answer)' },
            ]);
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        }
        setAsking(false);
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', height: 'calc(100vh - 120px)', minHeight: '480px' }}>
            <SectionTitle
                title='Ask Elariom'
                blurb={`Ask about ${org?.title || 'this workspace'}: rates, lanes, live carrier offers, prices with your margin. The same Elariom your team talks to in Teams.`}
                right={messages.length ? <Button variant='outlined' size='small' onClick={() => { setMessages([]); setError(null); }} disabled={asking}>New conversation</Button> : null}
            />
            <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', padding: '4px 2px' }}>
                {!messages.length && (
                    <Box sx={{ margin: 'auto', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '14px', color: theme.palette.text.secondary }}>Try one of these, or ask your own question.</Typography>
                        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                            {EXAMPLES.map(q => (
                                <Button key={q} variant='outlined' size='small' onClick={() => ask(q)} sx={{ textTransform: 'none' }}>{q}</Button>
                            ))}
                        </Box>
                    </Box>
                )}
                {messages.map((m, i) => <Bubble key={i} role={m.role} text={m.text} update={m.update} />)}
                {asking && (
                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                        Elariom is working on it… a search of live carrier offers can take up to a minute.
                    </Typography>
                )}
                {error && <Typography sx={{ fontSize: '12px', color: theme.palette.error.main }}>Elariom couldn't answer: {error}</Typography>}
                <div ref={endRef} />
            </Box>
            <Box
                component='form'
                onSubmit={e => { e.preventDefault(); ask(); }}
                sx={{ display: 'flex', gap: '8px', alignItems: 'flex-end', borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.15)}`, paddingTop: '12px' }}
            >
                <TextField
                    id='elariom-ask-input'
                    fullWidth
                    multiline
                    maxRows={6}
                    size='small'
                    placeholder='Ask Elariom…'
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
                    disabled={asking}
                />
                <Button type='submit' variant='contained' disabled={asking || !draft.trim()}>Send</Button>
            </Box>
        </Box>
    );
}

export default ElariomAsk;
